import { AskableIconLoading } from '@askable/ui/core/askable-icon';
import { datadogRum } from '@datadog/browser-rum';
import { useRouterState } from '@tanstack/react-router';
import { FeatureFlagsProvider, UserFeatureFlagProvider } from 'feature-flags';
import { Provider as JotaiProvider } from 'jotai';
import { NuqsAdapter } from 'nuqs/adapters/tanstack-router';
import { useCallback, useState } from 'react';
import { matchPath, Outlet } from 'src/lib/routing';
import { Provider } from 'urql';

import { AccessTokenSaver } from 'components/AccessTokenSaver';
import { DownloadManagerProvider } from 'components/DownloadManagerProvider/DownloadPollerProvider';
import { AppProvider } from 'components/common';
import { SidebarCollapsedProvider } from 'components/common/MainNavSidebar/hooks/useSidebarCollapsed';
import { AuthWrapper } from 'containers/Auth/AuthWrapper';
import { ConnectedClientProvider } from 'context/ConnectedClientContext';
import { globalVariables } from 'lib/globalVariables';
import { getUrqlClient, resetUrqlClient } from 'network/client';
import { UrqlResetContext } from 'network/urqlResetContext';

import type { StatsigImpressionListener } from 'feature-flags';
import type { FC } from 'react';

const isProd = import.meta.env.VITE_ENVIRONMENT === 'PROD';

const logFeatureFlagEvaluation: StatsigImpressionListener = ({ feature, value }) => {
  datadogRum.addFeatureFlagEvaluation(feature, value);
};

const statsigImpressionListener = isProd ? logFeatureFlagEvaluation : undefined;

// Providers that need a connected client / feature-flag context, ending in the
// `<Outlet />` that renders the matched route.
const ContextWrapper: FC = () => {
  // Selecting the id alone re-renders the provider stack only when the study changes, not on every
  // search-param write (nuqs syncs inputs to the URL per keystroke).
  const studyId = useRouterState({
    select: state => matchPath<'studyId'>('/studies/:studyId/*', state.location.pathname)?.params.studyId,
  });

  return (
    <ConnectedClientProvider>
      {({ isReady: clientIsReady, details }) => {
        if (!clientIsReady) {
          return <AskableIconLoading isFullScreen />;
        }

        return (
          <UserFeatureFlagProvider
            userId={details?.id ?? undefined}
            userAttributes={{
              team_id: details?.team?.id,
              study_id: studyId,
              type: (() => {
                if (details?.type?.researcher) {
                  return 'researcher';
                }
                if (details?.type?.client) {
                  return 'client';
                }
                return undefined;
              })(),
            }}
          >
            {({ isReady }) => {
              if (!isReady) {
                return <AskableIconLoading isFullScreen />;
              }
              return (
                <DownloadManagerProvider>
                  <AppProvider>
                    <SidebarCollapsedProvider>
                      <Outlet />
                    </SidebarCollapsedProvider>
                  </AppProvider>
                </DownloadManagerProvider>
              );
            }}
          </UserFeatureFlagProvider>
        );
      }}
    </ConnectedClientProvider>
  );
};

/**
 * The root route's component (mounted by `src/routes/__root.tsx`). It hosts
 * the whole provider stack so every provider that uses routing hooks
 * (NuqsAdapter, AccessTokenSaver, AuthWrapper, the connected-client / app
 * providers) sits inside the one router, with `<Outlet />` rendering the
 * matched route below.
 */
export const RootLayout = () => {
  const [client, setClient] = useState(getUrqlClient);
  const resetClient = useCallback(() => {
    setClient(resetUrqlClient());
  }, []);

  return (
    <NuqsAdapter>
      <AccessTokenSaver>
        <UrqlResetContext.Provider value={resetClient}>
          <Provider value={client}>
            <JotaiProvider>
              <FeatureFlagsProvider
                statsigImpressionListener={statsigImpressionListener}
                statsigClientKey={globalVariables.getEnvironmentVariables().statsigClientKey}
                environment={globalVariables.getEnvironmentVariables().statsigEnvironment}
                app="clients"
                attributes={{ demo: !!globalVariables.getEnvironmentVariables().isDemo }}
              >
                {({ isReady }) => {
                  if (!isReady) {
                    return <AskableIconLoading isFullScreen />;
                  }

                  return (
                    <AuthWrapper>
                      <ContextWrapper />
                    </AuthWrapper>
                  );
                }}
              </FeatureFlagsProvider>
            </JotaiProvider>
          </Provider>
        </UrqlResetContext.Provider>
      </AccessTokenSaver>
    </NuqsAdapter>
  );
};
